/**
 * Operator hotel detail page — a single hotel's identity (with its owning
 * partner), its real KPIs + recent calls, an optional provider-diagnostics
 * section (flag-gated, clearly labelled SAMPLE), and a tucked-away impersonation
 * launcher for support. Renders ONLY canonical operator data from
 * `@hostesa/api-client` (gated by `admin:cross_tenant_read`); the call list is
 * diagnostics only. Mirrors the calm loading / error / empty styling of the
 * oversight list pages and the prototype's data-first layout.
 */

import { type OperatorHotel, useOperatorCalls, useOperatorHotel } from '@hostesa/api-client';
import { useFlag } from '@hostesa/feature-flags';
import { useFormatters, useT } from '@hostesa/i18n';
import {
  Button,
  Card,
  cn,
  EmptyState,
  ErrorState,
  LanguagePill,
  LoadingState,
  MetricCard,
  type PillTone,
  StatusPill,
} from '@hostesa/ui';
import { PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import { useState } from 'react';
import { HotelSetupSection } from './hotel-setup-section';
import { ImpersonateLauncher } from './impersonate-launcher';

const PAGE_WRAP = 'mx-auto w-full max-w-6xl px-4 py-6 sm:px-8 sm:py-8';
const ROW = cn(
  'flex w-full flex-wrap items-center gap-3 border-b border-[var(--color-line-2)] px-3 py-3 last:border-0',
);
const ICON =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-canvas)] text-[var(--color-stone-600)]';
const META = 'font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]';
const EYEBROW =
  'mb-1 font-[var(--font-mono)] text-[10px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)]';
const SECTION_EYEBROW =
  'mb-3 flex items-center gap-2.5 font-[var(--font-mono)] text-[10px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)] before:h-px before:w-[18px] before:bg-[var(--color-line)]';

const VIEWER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

function hotelTone(status: OperatorHotel['status']): PillTone {
  if (status === 'active') return 'success';
  if (status === 'suspended') return 'alert';
  return 'neutral';
}
function callTone(status: string): PillTone {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'alert';
  if (status === 'handoff_requested' || status === 'human_active') return 'accent';
  return 'neutral';
}
function toSeconds(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function langCode(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim().toUpperCase() : null;
}

/** A label → mono-value definition row inside a diagnostics card. */
function DefRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-t border-[var(--color-line-2)] py-2.5 text-[13.5px] first:border-t-0">
      <span className="text-[var(--color-stone-mid)]">{label}</span>
      <span className="text-right font-[var(--font-mono)] text-[12px] text-[var(--color-charcoal)]">
        {value}
      </span>
    </div>
  );
}

export interface HotelDetailPageProps {
  hotelId: string;
  /** Optional back handler (router wires navigation). */
  onBack?: () => void;
}

export function HotelDetailPage({ hotelId, onBack }: HotelDetailPageProps) {
  const { t } = useT('backoffice');
  const { formatDuration } = useFormatters(VIEWER_TZ);
  const showDiagnostics = useFlag('backoffice_provider_diagnostics');
  const [supportOpen, setSupportOpen] = useState(false);

  const hotel = useOperatorHotel(hotelId);
  const calls = useOperatorCalls({ hotelId });

  if (hotel.isLoading) {
    return (
      <section data-testid="hotel-detail-page" className={PAGE_WRAP}>
        <LoadingState data-testid="hotel-detail-loading" title={t('oversight.loading')} />
      </section>
    );
  }
  if (hotel.isError || !hotel.data) {
    return (
      <section data-testid="hotel-detail-page" className={PAGE_WRAP}>
        <ErrorState
          data-testid="hotel-detail-error"
          title={t('oversight.error.title')}
          description={t('oversight.error.description')}
        />
      </section>
    );
  }

  const h = hotel.data;
  const partner = h.tenantName ?? t('oversight.hotels.unknownPartner');
  const callRows = calls.data?.items ?? [];

  return (
    <section data-testid="hotel-detail-page" className={PAGE_WRAP}>
      {/* Header --------------------------------------------------------------- */}
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className={EYEBROW}>
            {`${t('oversight.hotelDetail.eyebrow')} › ${t('oversight.hotelDetail.crumb')}`}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-[var(--font-display)] text-2xl text-[var(--color-charcoal)]">
              {h.name}
            </h1>
            <StatusPill
              dot
              tone={hotelTone(h.status)}
              label={t(`oversight.hotels.status.${h.status}`)}
            />
          </div>
          <p className="mt-1 text-sm text-[var(--color-stone-mid)]">
            {`${h.slug} · ${t('oversight.hotelDetail.partnerLabel')}: ${partner}${
              h.tenantSlug ? ` (${h.tenantSlug})` : ''
            }`}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {onBack && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="hotel-detail-back-btn"
              onClick={onBack}
            >
              {t('oversight.hotelDetail.backToHotels')}
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" data-testid="hotel-detail-resync-btn">
            {t('oversight.hotelDetail.resync')}
          </Button>
        </div>
      </header>

      {/* KPI row -------------------------------------------------------------- */}
      <div
        data-testid="hotel-detail-kpis"
        className={cn('mb-6 grid gap-4', showDiagnostics ? 'sm:grid-cols-4' : 'sm:grid-cols-2')}
      >
        <MetricCard
          data-testid="hotel-detail-kpi-calls24h"
          label={t('oversight.hotelDetail.kpi.calls24h')}
          value={h.calls24h}
        />
        <MetricCard
          data-testid="hotel-detail-kpi-tenant"
          label={t('oversight.hotelDetail.kpi.tenant')}
          value={<span className="text-xl">{partner}</span>}
        />
        {showDiagnostics && (
          <>
            <MetricCard
              data-testid="hotel-detail-kpi-lines"
              label={`${t('oversight.hotelDetail.kpi.linesOnline')} · ${t('oversight.hotelDetail.diagnostics.sampleNote')}`}
              value={<span className="text-xl">3 / 4</span>}
            />
            <MetricCard
              data-testid="hotel-detail-kpi-region"
              label={`${t('oversight.hotelDetail.kpi.region')} · ${t('oversight.hotelDetail.diagnostics.sampleNote')}`}
              value={<span className="text-xl">eu-central-1</span>}
            />
          </>
        )}
      </div>

      {/* Diagnostics (flag-gated, labelled SAMPLE) --------------------------- */}
      {showDiagnostics && (
        <section data-testid="hotel-detail-diagnostics" className="mb-6">
          <p className={SECTION_EYEBROW}>{t('oversight.hotelDetail.diagnostics.title')}</p>
          <Card className="p-4 sm:p-5">
            <p
              data-testid="hotel-detail-diagnostics-note"
              className="mb-2 font-[var(--font-mono)] text-[10px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)]"
            >
              {t('oversight.hotelDetail.diagnostics.sampleNote')}
            </p>
            <DefRow
              label={t('oversight.hotelDetail.diagnostics.provider')}
              value="sample-voice-rtc"
            />
            <DefRow
              label={t('oversight.hotelDetail.diagnostics.sipTrunk')}
              value="sip:sample-trunk-04"
            />
            <DefRow
              label={t('oversight.hotelDetail.diagnostics.lastHeartbeat')}
              value="12s ago (sample)"
            />
            <DefRow
              label={t('oversight.hotelDetail.diagnostics.knowledgeBase')}
              value="kb-rev-318 (sample)"
            />
            <DefRow
              label={t('oversight.hotelDetail.diagnostics.activeScripts')}
              value="4 (sample)"
            />
          </Card>
        </section>
      )}

      <HotelSetupSection hotelId={hotelId} />

      {/* Recent calls -------------------------------------------------------- */}
      <p className={SECTION_EYEBROW}>{t('oversight.hotelDetail.callsTitle')}</p>
      <Card className="mb-6 p-2 sm:p-3">
        {callRows.length === 0 ? (
          <EmptyState
            data-testid="hotel-detail-calls-empty"
            icon={<PhoneIncoming />}
            title={t('oversight.hotelDetail.callsEmpty')}
          />
        ) : (
          <ul data-testid="hotel-detail-calls" className="flex flex-col">
            {callRows.map((c) => {
              const dur = toSeconds(c.durationSeconds);
              const lang = langCode(c.language);
              const DirectionIcon = c.direction === 'outbound' ? PhoneOutgoing : PhoneIncoming;
              return (
                <li key={c.id}>
                  <div data-testid={`hotel-detail-call-row-${c.id}`} className={ROW}>
                    <span aria-hidden="true" className={ICON}>
                      <DirectionIcon className="size-4" strokeWidth={1.3} />
                    </span>
                    <StatusPill
                      dot
                      tone={callTone(c.status)}
                      label={t(`oversight.calls.status.${c.status}`)}
                    />
                    <span className={cn(META, 'min-w-0 flex-1 truncate')} title={c.id}>
                      {c.id}
                    </span>
                    {lang && <LanguagePill code={lang} />}
                    <span className={cn(META, 'w-14 text-right')}>
                      {dur !== null ? formatDuration(dur) : t('oversight.notAvailable')}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* Support session (impersonation, tucked behind a toggle) ------------- */}
      <div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          data-testid="hotel-detail-support-toggle"
          aria-expanded={supportOpen}
          onClick={() => setSupportOpen((v) => !v)}
        >
          {supportOpen
            ? t('oversight.hotelDetail.closeSupportSession')
            : t('oversight.hotelDetail.openSupportSession')}
        </Button>
        {supportOpen && (
          <div className="mt-4">
            <ImpersonateLauncher tenantId={h.tenantId} hotelId={hotelId} />
          </div>
        )}
      </div>
    </section>
  );
}
